/**
 * Shared `[PROPOSED]` module scaffolding for runtime/observability resources
 * whose backing API has not shipped yet (`frontend-entity-coverage §2.3`,
 * design-system §5.5 / §7).
 *
 * The design law for a not-yet-backed surface is *degrade, never crash*: a
 * resource that is named in the acceptance matrix but whose endpoint is
 * `[PROPOSED]` must still be **discoverable** (nav / ⌘K), render a calm
 * labeled-empty teaser, and never throw. These two components give every such
 * module one identical, honest empty state:
 *
 *   - `ProposedListPage`  — a list screen that names its backing endpoint and
 *     shows the `Empty` primitive (the only place `border-dashed` is allowed).
 *   - `ProposedShowTeaser` — an Inspector `Body` block (E0, no card) that names
 *     the backing endpoint for a `:id` route reached before the API exists.
 *
 * Both are intentionally dependency-light (shadcn `ui` primitives + HugeIcons
 * only) and self-contained so they compile regardless of D1/worker progress.
 */
import * as React from "react";
import { useGo } from "@refinedev/core";
import { ArrowLeft, type IconComponent } from "@/components/ui/icon";

import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { PageHeader } from "../projects/_shared";

export interface ProposedListPageProps {
  /** Screen title (e.g. "Event Sources"). */
  title: string;
  /** One-line description under the title. */
  description: string;
  /** HugeIcons-compatible icon for the empty-state media. */
  icon: IconComponent;
  /** The `[PROPOSED]` backing endpoint this module will read once it ships. */
  endpoint: string;
  /** Empty-state title line (defaults to "Not available yet"). */
  emptyTitle?: string;
  /** Empty-state description (defaults to a generic "ships with…" line). */
  emptyDescription?: React.ReactNode;
  /** Optional extra content rendered under the description (e.g. a CTA). */
  children?: React.ReactNode;
  /** Stable test id on the empty block (for nav/discoverability assertions). */
  testId?: string;
}

/**
 * A discoverable list screen for a `[PROPOSED]` resource. Renders the standard
 * page header + the `Empty` primitive naming the backing endpoint. Never errors.
 */
export function ProposedListPage({
  title,
  description,
  icon: Icon,
  endpoint,
  emptyTitle = "Not available yet",
  emptyDescription,
  children,
  testId,
}: ProposedListPageProps) {
  return (
    <div className="space-y-6">
      <PageHeader title={title} description={description} />
      <Empty data-testid={testId}>
        <EmptyHeader>
          <EmptyMedia>
            <Icon />
          </EmptyMedia>
          <EmptyTitle>{emptyTitle}</EmptyTitle>
          <EmptyDescription>
            {emptyDescription ?? (
              <>
                This surface lights up once{" "}
                <code className="font-mono text-xs">{endpoint}</code> ships.{" "}
                <span className="text-xs">([PROPOSED])</span>
              </>
            )}
          </EmptyDescription>
        </EmptyHeader>
        {children ? <EmptyContent>{children}</EmptyContent> : null}
      </Empty>
    </div>
  );
}

export interface ProposedShowTeaserProps {
  /** The `[PROPOSED]` backing endpoint this show page will read once it ships. */
  endpoint: string;
  /** Optional override note. */
  note?: React.ReactNode;
}

/**
 * Inspector `Body`-block teaser (E0, no card) for a `[PROPOSED]` show page —
 * the §5.5 muted labeled-empty note. Used inside the `Inspector` grammar so a
 * proposed entity's show still reads as a real Inspector screen.
 */
export function ProposedShowTeaser({ endpoint, note }: ProposedShowTeaserProps) {
  return (
    <p className="text-sm text-muted-foreground">
      {note ?? (
        <>
          Full detail available once{" "}
          <code className="font-mono text-xs">{endpoint}</code> ships{" "}
          <span className="text-xs">([PROPOSED])</span>.
        </>
      )}
    </p>
  );
}

/** A quiet "Back to list" ghost button for `[PROPOSED]` show routes. */
export function BackToList({ to, label }: { to: string; label: string }) {
  const go = useGo();
  return (
    <Button variant="ghost" size="sm" onClick={() => go({ to })}>
      <ArrowLeft className="mr-2 h-4 w-4" /> {label}
    </Button>
  );
}
